<template>
    <!-- translate="no": Chrome auto-translate wraps text in <font> tags and breaks Vue's DOM patching -->
    <div v-show="isStandard" class="cvt-fleet-adjustment" translate="no">
        <div class="cvt-fleet-adjustment__toolbar mb-3">
            <b-button variant="primary" class="mr-2" @click="openModal()">
                <i class="la la-plus mr-2"></i>AGGIUNGI VEICOLO
            </b-button>
            <b-form-input v-model="search"
                          class="cvt-fleet-adjustment__search ml-auto"
                          placeholder="Cerca per targa, marca o modello"
                          type="search"/>
        </div>

        <div class="table-responsive">
            <table class="table table-striped cvt-fleet-adjustment__table mb-2">
                <thead>
                <tr>
                    <th>Targa</th>
                    <th>Marca / Modello</th>
                    <th>Anno</th>
                    <th class="text-right">Valore assicurato</th>
                    <th v-for="cover in covers" :key="cover.codice" :title="cover.descrizione" class="text-center">
                        {{ cover.codice }}
                    </th>
                    <th class="text-right">Azioni</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in paged" :key="row.key" :class="rowClass(row)">
                    <td>
                        <del v-if="row.removed">{{ row.vehicle.targa }}</del>
                        <span v-else>{{ row.vehicle.targa }}</span>
                    </td>
                    <td>{{ marcaModello(row.vehicle) }}</td>
                    <td>{{ row.vehicle.anno_immatricolazione }}</td>
                    <td class="text-right">{{ money(row.vehicle.valore_assicurato) }}</td>
                    <td v-for="cover in covers" :key="cover.codice" class="text-center"
                        :class="{'cvt-fleet-adjustment__cover--new': coverIsNew(row, cover)}">
                        <b-form-checkbox
                            :checked="coverChecked(row, cover)"
                            :disabled="coverDisabled(row, cover)"
                            @change="toggleCover(row, cover)"/>
                    </td>
                    <td class="text-right text-nowrap">
                        <template v-if="row.origin === 'added'">
                            <b-button variant="link" size="sm" class="p-0 mr-2" title="Modifica" @click="openModal(row.index)">
                                <i class="la la-edit"></i>
                            </b-button>
                            <b-button variant="link" size="sm" class="text-danger p-0" title="Rimuovi" @click="removeAdded(row.index)">
                                <i class="la la-trash"></i>
                            </b-button>
                        </template>
                        <template v-else-if="canRemove">
                            <b-button v-if="!row.removed" variant="link" size="sm" class="text-danger p-0" title="Escludi dalla copertura" @click="toggleRemove(row._id)">
                                <i class="la la-ban"></i> Escludi
                            </b-button>
                            <b-button v-else variant="link" size="sm" class="p-0" title="Ripristina" @click="toggleRemove(row._id)">
                                <i class="la la-undo"></i> Ripristina
                            </b-button>
                        </template>
                    </td>
                </tr>
                <tr v-if="!filtered.length">
                    <td :colspan="columnsCount" class="text-center text-muted">
                        {{ search ? 'Nessun veicolo corrisponde alla ricerca.' : 'Nessun veicolo.' }}
                    </td>
                </tr>
                </tbody>
                <tfoot>
                <tr class="table-active">
                    <th>Totale</th>
                    <th>{{ activeCount }} {{ activeCount === 1 ? 'veicolo' : 'veicoli' }}</th>
                    <th></th>
                    <th class="text-right">{{ money(activeValore) }}</th>
                    <th :colspan="covers.length + 1"></th>
                </tr>
                </tfoot>
            </table>
        </div>

        <div class="cvt-fleet-adjustment__legend text-muted small mb-2">
            <span class="cvt-fleet-adjustment__chip cvt-fleet-adjustment__chip--added"></span> Nuovo veicolo
            <span class="cvt-fleet-adjustment__chip cvt-fleet-adjustment__chip--removed ml-3"></span> Veicolo escluso
            <span class="cvt-fleet-adjustment__chip cvt-fleet-adjustment__chip--changed ml-3"></span> Garanzia aggiunta
        </div>

        <div v-if="totalPages > 1" class="d-flex align-items-center mb-3">
            <b-button size="sm" variant="outline-secondary" class="mr-2" :disabled="page <= 1" @click="page--">Prec.</b-button>
            <span class="small text-muted">Pagina {{ page }} di {{ totalPages }}</span>
            <b-button size="sm" variant="outline-secondary" class="ml-2" :disabled="page >= totalPages" @click="page++">Succ.</b-button>
        </div>

        <adjustment-premium-preview
            :preview-url="previewUrl"
            :policy-id="policyId"
            :changes="changes"
            :adjustment-date="adjustmentDate"/>

        <vehicle-form-modal ref="vehicleModal"
                            :available-covers="covers"
                            :ricovero-options="ricoveroOptions"
                            @save="onVehicleSave"/>
    </div>
</template>

<script>
import VehicleFormModal from "@/components/cvt/vehicles/VehicleFormModal";
import AdjustmentPremiumPreview from "./AdjustmentPremiumPreview";
import objectToOptions from "@/commons/objectToOptions";

export default {
    name: 'CvtFleetAdjustment',
    components: {VehicleFormModal, AdjustmentPremiumPreview},
    props: {
        fleet: {type: Array, default: () => []},
        covers: {type: Array, default: () => []},
        canRemove: {type: Boolean, default: false},
        previewUrl: {type: String, required: true},
        policyId: {type: String, required: true},
        ricoveroEnum: {type: Object, default: () => ({})},
    },
    data() {
        return {
            adjustmentType: 'standard',
            adjustmentDate: '',
            added: [],
            removedIds: [],
            coverSelections: {},
            search: '',
            page: 1,
            perPage: 25,
        }
    },
    computed: {
        isStandard() {
            return this.adjustmentType === 'standard'
        },
        mandatoryCovers() {
            return this.covers.filter(cover => cover.mandatory).map(cover => cover.codice)
        },
        ricoveroOptions() {
            return objectToOptions(this.ricoveroEnum)
        },
        rows() {
            const current = this.fleet.map(vehicle => ({
                key: 'cur-' + vehicle._id,
                origin: 'current',
                _id: vehicle._id,
                vehicle,
                removed: this.removedIds.includes(vehicle._id),
            }))
            const added = this.added.map((vehicle, index) => ({
                key: 'add-' + index,
                origin: 'added',
                index,
                vehicle,
                removed: false,
            }))
            return [...current, ...added]
        },
        filtered() {
            const term = this.search.trim().toLowerCase()
            if (!term) return this.rows
            return this.rows.filter(row => {
                const v = row.vehicle
                return [v.targa, v.marca, v.modello].some(value => value && String(value).toLowerCase().includes(term))
            })
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.filtered.length / this.perPage))
        },
        paged() {
            const start = (this.page - 1) * this.perPage
            return this.filtered.slice(start, start + this.perPage)
        },
        columnsCount() {
            return 5 + this.covers.length
        },
        activeCount() {
            return (this.fleet.length - this.removedIds.length) + this.added.length
        },
        activeValore() {
            const current = this.fleet
                .filter(v => !this.removedIds.includes(v._id))
                .reduce((acc, v) => acc + (parseFloat(v.valore_assicurato) || 0), 0)
            const added = this.added.reduce((acc, v) => acc + (parseFloat(v.valore_assicurato) || 0), 0)
            return current + added
        },
        coverageChanges() {
            return this.fleet.reduce((acc, vehicle) => {
                if (this.removedIds.includes(vehicle._id)) return acc
                const current = vehicle.covers || []
                const selected = this.coverSelections[vehicle._id] || current
                const addedCovers = selected.filter(codice => !current.includes(codice))
                if (addedCovers.length) acc.push({_id: vehicle._id, covers: [...selected]})
                return acc
            }, [])
        },
        changes() {
            return {
                added: this.added,
                removed: this.removedIds,
                coverage_changes: this.coverageChanges,
            }
        },
        emittedChanges() {
            return this.isStandard ? this.changes : {added: [], removed: [], coverage_changes: []}
        },
    },
    watch: {
        emittedChanges: {
            deep: true,
            handler(value) {
                this.$emit('change', value)
            },
        },
        'filtered.length'() {
            if (this.page > this.totalPages) this.page = this.totalPages
        },
        search() {
            this.page = 1
        },
    },
    created() {
        this.fleet.forEach(vehicle => {
            this.$set(this.coverSelections, vehicle._id, [...(vehicle.covers || [])])
        })
    },
    mounted() {
        this.bindAdjustmentType()
        this.bindBackpackField('adjustment_date', el => {
            this.adjustmentDate = el.value
            const update = e => { this.adjustmentDate = e.target.value }
            el.addEventListener('change', update)
            el.addEventListener('input', update)
        })
        this.applyTypeVisibility()
        this.$emit('change', this.emittedChanges)
    },
    methods: {
        money(value) {
            return new Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(Number(value) || 0)
        },
        marcaModello(vehicle) {
            return [vehicle.marca, vehicle.modello].filter(Boolean).join(' ')
        },
        rowClass(row) {
            if (row.removed) return 'table-danger'
            if (row.origin === 'added') return 'table-success'
            if (this.hasAddedCovers(row)) return 'table-warning'
            return ''
        },
        hasAddedCovers(row) {
            if (row.origin !== 'current') return false
            const original = row.vehicle.covers || []
            const selected = this.coverSelections[row._id] || original
            return selected.some(codice => !original.includes(codice))
        },
        coverList(row) {
            if (row.origin === 'added') return row.vehicle.covers || []
            return this.coverSelections[row._id] || row.vehicle.covers || []
        },
        coverChecked(row, cover) {
            return cover.mandatory || this.coverList(row).includes(cover.codice)
        },
        coverDisabled(row, cover) {
            if (row.removed) return true
            if (cover.mandatory) return true
            //an existing vehicle's already-active covers can never be removed
            if (row.origin === 'current') return (row.vehicle.covers || []).includes(cover.codice)
            return false
        },
        coverIsNew(row, cover) {
            if (row.origin !== 'current') return false
            const original = row.vehicle.covers || []
            return this.coverChecked(row, cover) && !original.includes(cover.codice) && !cover.mandatory
        },
        toggleCover(row, cover) {
            if (this.coverDisabled(row, cover)) return
            if (row.origin === 'added') {
                const current = row.vehicle.covers || []
                this.$set(row.vehicle, 'covers', current.includes(cover.codice)
                    ? current.filter(c => c !== cover.codice)
                    : [...current, cover.codice])
                return
            }
            const current = this.coverSelections[row._id] || [...(row.vehicle.covers || [])]
            const next = current.includes(cover.codice)
                ? current.filter(c => c !== cover.codice)
                : [...current, cover.codice]
            this.$set(this.coverSelections, row._id, next)
        },
        toggleRemove(id) {
            this.removedIds = this.removedIds.includes(id)
                ? this.removedIds.filter(item => item !== id)
                : [...this.removedIds, id]
        },
        openModal(index = null) {
            const vehicle = index === null ? null : this.added[index]
            this.$refs.vehicleModal.open(vehicle, index)
        },
        onVehicleSave({vehicle, index}) {
            if (index === null) {
                this.added.push(vehicle)
            } else {
                this.$set(this.added, index, vehicle)
            }
        },
        removeAdded(index) {
            this.added.splice(index, 1)
        },
        bindBackpackField(name, callback) {
            const el = document.querySelector('[name="' + name + '"]')
            if (el) callback(el)
        },
        bindAdjustmentType() {
            //Backpack syncs the radio into a hidden input via jQuery .change() (native listeners
            //miss it), so read the initial value there but listen on the radios
            const hidden = document.querySelector('input[type="hidden"][name="adjustment_type"]')
            if (!hidden) return
            this.adjustmentType = hidden.value
            const wrapper = hidden.closest('.form-group') || hidden.parentNode
            wrapper.querySelectorAll('input[type="radio"]').forEach(radio => {
                radio.addEventListener('change', e => {
                    if (!e.target.checked) return
                    this.adjustmentType = e.target.value
                    this.applyTypeVisibility()
                })
            })
        },
        applyTypeVisibility() {
            const manualField = document.querySelector('[name="manual_amount"]')
            const manualWrapper = manualField ? manualField.closest('.form-group') : null
            if (manualWrapper) manualWrapper.style.display = this.isStandard ? 'none' : ''

            //manual hides the whole standard field, label included
            const fleetWrapper = this.$el.closest('.form-group')
            if (fleetWrapper) fleetWrapper.style.display = this.isStandard ? '' : 'none'
        },
    },
}
</script>

<style lang="scss">
//.form-floating base style is SPA-only (app.css), not loaded in admin; inject it for the reused inputs
@import "@sass/vue-component";
@import "@sass/commons/form-floating";

//<float-select> is a treeselect: neutralise form-floating's padding so its label floats inside, not above
.form-floating.float-select > .form-control.af-select {
    padding: 0;
}

.form-floating.float-select .vue-treeselect__control {
    padding-top: 1.125rem;
}

.cvt-fleet-adjustment {
    &__toolbar {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
    }

    &__search {
        max-width: 320px;
    }

    &__table td,
    &__table th {
        vertical-align: middle;
        white-space: nowrap;
    }

    &__cover--new {
        background-color: rgba(40, 167, 69, 0.18);
    }

    &__chip {
        display: inline-block;
        width: 0.9rem;
        height: 0.9rem;
        border-radius: 2px;
        vertical-align: middle;

        &--added { background-color: rgba(40, 167, 69, 0.4); }
        &--removed { background-color: rgba(220, 53, 69, 0.4); }
        &--changed { background-color: rgba(255, 193, 7, 0.5); }
    }
}
</style>
